import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, FileText, Save, ExternalLink, Mail, Phone, Building, Briefcase, FileCheck2, Check } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "nouveau", l: "Nouveau", c: "bg-[#FFC107]" },
  { v: "en_cours", l: "En cours", c: "bg-blue-100" },
  { v: "valide", l: "Validé", c: "bg-emerald-200" },
  { v: "annule", l: "Annulé", c: "bg-red-100" },
];

const fileUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

const fmtMoney = (v) => v ? `${parseFloat(v).toLocaleString("fr-FR")} €` : "—";
const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR") : "—";

export default function ReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("nouveau");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/reservations/${id}`).then(res => {
      if (cancelled) return;
      setR(res.data);
      setNotes(res.data.notes || "");
      setStatus(res.data.status || "nouveau");
    }).catch(() => toast.error("Réservation introuvable"));
    return () => { cancelled = true; };
  }, [id]);

  const load = () => api.get(`/admin/reservations/${id}`).then(res => {
    setR(res.data);
    setNotes(res.data.notes || "");
    setStatus(res.data.status || "nouveau");
  }).catch(() => toast.error("Réservation introuvable"));

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/reservations/${id}`, { status, notes });
      toast.success("Réservation mise à jour");
      load();
    } catch { toast.error("Échec"); }
    finally { setSaving(false); }
  };

  if (!r) return <div className="p-24 text-center font-mono text-sm">Chargement...</div>;
  const ci = r.company_info || {};
  const bi = r.bilan || {};
  const docs = bi.documents || [];
  const companyAddress = [ci.address, ci.postal_code, ci.city].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Retour au tableau de bord
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFC107] mb-2">— Réservation {fmtDate(r.created_at)}</div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{r.property_title}</h1>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
              <strong>{r.full_name}</strong> · {r.type} · dossier {r.flow_mode || "—"}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <select value={status} onChange={e => setStatus(e.target.value)} className="input" data-testid="rd-status">
              {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <button onClick={saveStatus} disabled={saving} data-testid="rd-save"
              className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-50">
              {saving ? "..." : "Sauver"} <Save className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card icon={Mail} title="Contact">
            <Row label="Nom" value={r.full_name} />
            <Row label="Email" value={<a href={`mailto:${r.email}`} className="underline">{r.email}</a>} />
            <Row label="Téléphone" value={<a href={`tel:${r.phone}`} className="underline">{r.phone}</a>} />
            <Row label="Date de visite souhaitée" value={r.preferred_visit_date} />
            <Row label="Date d'entrée souhaitée" value={r.start_date} />
            <Row label="Durée souhaitée" value={r.duration_months ? `${r.duration_months} mois` : "—"} />
          </Card>

          <Card icon={Briefcase} title="Activité">
            <Row label="Type d'activité" value={r.activity_type} />
            <Row label="Surface souhaitée" value={r.requested_surface ? `${r.requested_surface} m²` : "—"} />
            {r.activity_details && (
              <div className="mt-3">
                <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1">Détails</div>
                <p className="text-sm whitespace-pre-line bg-slate-50 p-3 border-l-4 border-[#FFC107]">{r.activity_details}</p>
              </div>
            )}
          </Card>

          <Card icon={Building} title="Entreprise">
            <Row label="Raison sociale" value={ci.legal_name} />
            <Row label="SIRET" value={ci.siret && <span className="font-mono">{ci.siret}</span>} />
            <Row label="Dirigeant" value={ci.director_name} />
            <Row label="Adresse" value={companyAddress} />
          </Card>

          <Card icon={FileCheck2} title="Bilan & documents">
            <Row label="Chiffre d'affaires" value={fmtMoney(bi.revenue)} />
            <Row label="Résultat net" value={fmtMoney(bi.net_profit)} />
            <Row label="Effectif" value={bi.employees} />
            <Row label="Exercice" value={bi.fiscal_year} />
            {bi.notes && (
              <div className="mt-3">
                <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-1">Notes</div>
                <p className="text-sm whitespace-pre-line bg-slate-50 p-3 border-l-4 border-[#FFC107]">{bi.notes}</p>
              </div>
            )}
            {docs.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-2">Documents joints ({docs.length})</div>
                <div className="space-y-2">
                  {docs.map((d, i) => (
                    <a key={i} href={fileUrl(d.url)} target="_blank" rel="noreferrer" data-testid={`rd-doc-${i}`}
                      className="flex items-center justify-between border border-slate-900/15 p-3 text-sm hover:bg-[color:var(--solar)] transition-colors">
                      <span className="flex items-center gap-2 truncate"><FileText className="w-4 h-4 shrink-0" /><span className="truncate">{d.name}</span>{d.size && <span className="text-xs opacity-50 shrink-0">({Math.round(d.size/1024)} Ko)</span>}</span>
                      <ExternalLink className="w-4 h-4 opacity-60 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        {r.message && (
          <div className="mt-6 bg-[color:var(--bg-surface)] border p-6" style={{ borderColor: "var(--border-default)" }}>
            <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-2">Message libre du client</div>
            <p className="text-sm whitespace-pre-line">{r.message}</p>
          </div>
        )}

        <div className="mt-6 bg-[color:var(--bg-surface)] border p-6" style={{ borderColor: "var(--border-default)" }}>
          <div className="text-xs font-bold uppercase tracking-widest opacity-60 mb-2">Notes internes</div>
          <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} className="input resize-none w-full" placeholder="Notes pour l'équipe..." data-testid="rd-notes" />
        </div>

        {r.payment_status && r.payment_status !== "non_requis" && (
          <div className="mt-6 bg-emerald-50 border-l-4 border-emerald-500 p-4 text-sm flex items-center gap-2">
            <Check className="w-5 h-5 text-emerald-700" /> <strong>Paiement :</strong> {r.payment_status}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-[color:var(--bg-surface)] border p-6" style={{ borderColor: "var(--border-default)" }}>
    <div className="flex items-center gap-3 mb-4">
      <Icon className="w-5 h-5 text-[#FFC107]" />
      <h2 className="font-display font-bold text-lg">{title}</h2>
    </div>
    <div className="space-y-2 text-sm">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3">
    <span className="text-xs font-bold uppercase tracking-widest opacity-60">{label}</span>
    <span className="text-sm">{value || "—"}</span>
  </div>
);
